import merge from 'lodash/merge';
import { useCallback, useState } from 'react';
/**
 * Generic hook for managing multiple related state values as a single object.
 * Provides utilities to update and reset the state.
 *
 * @template T - The type of the state object
 * @param initialState - The initial state object
 * @returns A tuple containing [state, resetState, updateState]
 * - state: The current state object
 * - resetState: Function to reset state to initial values
 * - updateState: Function to partially update state (merges with current state)
 *
 * @example
 * const [filters, resetFilters, updateFilters] = useMultistate({
 *   searchQuery: '',
 *   statuses: [],
 *   categories: []
 * });
 *
 * // Update single field
 * updateFilters({ searchQuery: 'test' });
 *
 * // Update multiple fields
 * updateFilters({ searchQuery: 'test', statuses: ['active'] });
 *
 * // Reset to initial state
 * resetFilters();
 */ export function useMultistate(initialState) {
    const [state, setState] = useState(initialState);
    const resetState = useCallback(()=>{
        setState(initialState);
    }, [
        initialState
    ]);
    const updateState = useCallback((partial)=>{
        setState((prev)=>merge({}, prev, partial));
    }, []);
    return [
        state,
        resetState,
        updateState
    ];
}
